import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Generate or retrieve anonymous user ID
const getAnonymousUserId = () => {
  let userId = localStorage.getItem("cosmovex_anonymous_id");
  if (!userId) {
    // Generate a unique ID using timestamp + random string
    userId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem("cosmovex_anonymous_id", userId);
  }
  return userId;
};

const LikeButton = ({ targetType, targetId, initialLikesCount = 0 }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [isLoading, setIsLoading] = useState(false);
  const [anonymousId] = useState(getAnonymousUserId());

  useEffect(() => {
    checkIfLiked();
  }, [targetType, targetId]);

  const checkIfLiked = async () => {
    try {
      // Query Likes collection using anonymous ID
      const likesQuery = query(
        collection(db, "Likes"),
        where("anonymousId", "==", anonymousId),
        where("targetType", "==", targetType),
        where("targetId", "==", targetId)
      );
      const likesSnapshot = await getDocs(likesQuery);

      setIsLiked(!likesSnapshot.empty);
    } catch (error) {
      console.error("Error checking if liked:", error);
    }
  };

  const handleLikeClick = async () => {
    // Optimistic UI update
    const wasLiked = isLiked;
    const previousCount = likesCount;
    setIsLiked(!wasLiked);
    setLikesCount(wasLiked ? likesCount - 1 : likesCount + 1);
    setIsLoading(true);

    try {
      // Log parameters for debugging
      console.log("Toggle like params:", { targetType, targetId, anonymousId });

      const functions = getFunctions();
      const toggleLike = httpsCallable(functions, "toggleLike");

      const result = await toggleLike({
        targetType,
        targetId,
        anonymousId
      });

      console.log("Toggle like result:", result.data);

      // The function returns { liked: boolean }
      // We already updated optimistically, so just verify
      if (result.data.liked !== !wasLiked) {
        // Revert if mismatch
        setIsLiked(wasLiked);
        setLikesCount(previousCount);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        details: error.details
      });
      // Revert optimistic update on error
      setIsLiked(wasLiked);
      setLikesCount(previousCount);

      // Show more specific error message
      const errorMessage = error.message || "Failed to update like. Please try again.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Like Button */}
      <motion.button
        onClick={handleLikeClick}
        disabled={isLoading}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 md:gap-3 rounded-full border-2 transition-all ${
          isLiked
            ? "bg-gradient-to-r from-pink-500 to-purple-500 border-pink-500 text-white"
            : "bg-white/5 border-white/10 text-gray-300 hover:border-pink-500/50 hover:bg-white/10"
        } ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {/* Heart Icon */}
        <motion.svg
          animate={{ scale: isLiked ? [1, 1.2, 1] : 1 }}
          transition={{ duration: 0.3 }}
          className="w-5 h-5 md:w-6 md:h-6"
          fill={isLiked ? "currentColor" : "none"}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </motion.svg>

        {/* Like Count */}
        <span className="font-semibold text-sm md:text-base">
          {likesCount} {likesCount === 1 ? "Like" : "Likes"}
        </span>
      </motion.button>
    </div>
  );
};

export default LikeButton;
